/* ==========================================================================
   Onera — Global scripts

   Scroll-based reveal via IntersectionObserver. Elements marked with the
   .onera-reveal class fade + slide up as they enter the viewport. The
   initial hidden state in onera.css is gated on `html.js`, so if this
   script fails for any reason the site stays fully visible.
   ========================================================================== */
(function () {
  // Announce JS is active so the reveal's initial hidden state applies.
  document.documentElement.classList.add('js');

  const markImageLoaded = function (img) {
    if (!img) return;
    img.classList.add('is-loaded');
    var wrap = img.closest('.onera-img-skel, .onera-product-card__media, .onera-hero__slide, .onera-pdp__media');
    if (wrap) wrap.classList.add('is-loaded');
  };

  const initImageSkeletons = function (root) {
    var scope = root || document;
    var images = scope.querySelectorAll
      ? scope.querySelectorAll('.onera-img-skel img, .onera-product-card__media img, .onera-hero__image, .onera-pdp__media img')
      : [];
    images.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        markImageLoaded(img);
        return;
      }
      img.addEventListener('load', function () {
        markImageLoaded(img);
      });
      img.addEventListener('error', function () {
        markImageLoaded(img);
      });
    });
  };

  const prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  const isDesignMode = function () {
    return (
      (window.Shopify && window.Shopify.designMode) ||
      document.documentElement.classList.contains('shopify-design-mode') ||
      document.documentElement.classList.contains('onera-design-mode')
    );
  };

  const queryInScope = function (selector, root) {
    if (root && root.querySelectorAll) {
      return root.querySelectorAll(selector);
    }
    return document.querySelectorAll(selector);
  };

  const revealTargets = function (targets) {
    targets.forEach(function (el) {
      el.classList.add('onera-reveal--visible');
    });
  };

  let revealObserver = null;

  const initReveal = function (root) {
    const targets = queryInScope('.onera-reveal:not(.onera-reveal--visible)', root);
    if (!targets.length) return;

    if (isDesignMode() || prefersReducedMotion) {
      revealTargets(targets);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      revealTargets(targets);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.remove('onera-reveal--pending');
              entry.target.classList.add('onera-reveal--visible');
              obs.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -8% 0px'
        }
      );
    }

    targets.forEach(function (el) {
      if (
        el.classList.contains('onera-reveal--above-fold') ||
        el.classList.contains('onera-reveal--priority') ||
        el.closest('.onera-collection-hero')
      ) {
        el.classList.add('onera-reveal--visible');
        return;
      }
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) {
        el.classList.add('onera-reveal--visible');
        return;
      }
      el.classList.add('onera-reveal--pending');
      revealObserver.observe(el);
    });
  };

  /* Hero parallax: the background layer drifts at 40% of scroll speed so
     the hero gains subtle depth without feeling gimmicky. Wrapped in
     requestAnimationFrame and rate-limited with a ticking flag so we only
     write to the DOM once per frame. */
  const initParallax = function () {
    if (prefersReducedMotion) return;

    const hero = document.querySelector('.onera-hero__bg');
    if (!hero) return;

    const PARALLAX_RATE = 0.4;
    let ticking = false;

    const update = function () {
      hero.style.transform =
        'translate3d(0, ' + window.scrollY * PARALLAX_RATE + 'px, 0)';
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    update();
  };

  const initHeroSlideshow = function () {
    const hero = document.querySelector('[data-onera-hero-slideshow]');
    if (!hero) return;

    const slides = hero.querySelectorAll('.onera-hero__slide');
    if (slides.length < 2) return;

    const SLIDE_INTERVAL = 6000;
    let activeIndex = 0;

    window.setInterval(function () {
      slides[activeIndex].classList.remove('is-active');
      activeIndex = (activeIndex + 1) % slides.length;
      slides[activeIndex].classList.add('is-active');
    }, SLIDE_INTERVAL);
  };

  const initGetTheLook = function (root) {
    const buttons = queryInScope('[data-onera-look-add]:not([data-onera-look-add-init])', root);
    if (!buttons.length) return;

    const parseCsv = function (raw) {
      if (!raw) return [];
      return raw
        .split(',')
        .map(function (value) {
          return value.trim();
        })
        .filter(function (value) {
          return value.length > 0;
        });
    };

    const parseProductPayload = function (item) {
      const dataEl = item.querySelector('[data-look-product-variants]');
      if (!dataEl) return null;
      try {
        return JSON.parse(dataEl.textContent) || null;
      } catch (error) {
        return null;
      }
    };

    const resolveVariantForItem = function (item) {
      const payload = parseProductPayload(item);
      if (!payload || !payload.variants || !payload.variants.length) return null;

      const sizeIndex = Number(payload.size_option_index);
      const hasSizeOption = sizeIndex >= 0;
      const checked = item.querySelector('[data-look-size-choice]:checked');

      if (hasSizeOption) {
        if (!checked) {
          const anyEnabled = item.querySelector('[data-look-size-choice]:not(:disabled)');
          if (!anyEnabled) return { id: null, available: false, reason: 'sold_out' };
          return { id: null, available: false, reason: 'select' };
        }
        const sizeValue = String(checked.value || '').trim().toLowerCase();
        const availableMatch = payload.variants.find(function (variant) {
          if (!variant.available || !variant.options) return false;
          const optionValue = variant.options[sizeIndex];
          return optionValue && String(optionValue).toLowerCase() === sizeValue;
        });
        if (availableMatch) return { id: availableMatch.id, available: true, reason: null };

        const anyMatch = payload.variants.find(function (variant) {
          if (!variant.options) return false;
          const optionValue = variant.options[sizeIndex];
          return optionValue && String(optionValue).toLowerCase() === sizeValue;
        });
        if (anyMatch) return { id: anyMatch.id, available: false, reason: 'sold_out' };
        return { id: null, available: false, reason: 'sold_out' };
      }

      const defaultId = Number(payload.default_variant_id) || null;
      if (defaultId) {
        const defaultVariant = payload.variants.find(function (variant) {
          return Number(variant.id) === defaultId;
        });
        if (defaultVariant) {
          return {
            id: defaultVariant.id,
            available: !!defaultVariant.available,
            reason: defaultVariant.available ? null : 'sold_out'
          };
        }
      }

      const firstAvailable = payload.variants.find(function (variant) {
        return variant.available;
      });
      if (firstAvailable) return { id: firstAvailable.id, available: true, reason: null };
      return {
        id: payload.variants[0] ? payload.variants[0].id : null,
        available: false,
        reason: 'sold_out'
      };
    };

    const syncPanel = function (panel, button) {
      const items = panel.querySelectorAll('[data-look-product]');
      const resolved = Array.prototype.map.call(items, resolveVariantForItem);
      const messageEl = panel.querySelector('[data-look-availability-message]');
      const labelAdd = button.getAttribute('data-label-add') || button.textContent.trim();
      const labelSelect = button.getAttribute('data-label-select-sizes') || labelAdd;
      const labelSoldOut = button.getAttribute('data-label-sold-out') || labelAdd;

      items.forEach(function (item) {
        const checked = item.querySelector('[data-look-size-choice]:checked');
        const selectedEl = item.querySelector('[data-look-size-selected]');
        const sizeField = item.querySelector('[data-look-size-option]');
        if (!selectedEl) return;
        if (checked) {
          selectedEl.textContent = checked.value;
        } else {
          selectedEl.textContent =
            (sizeField && sizeField.getAttribute('data-label-select')) || 'Select size';
        }
      });

      if (!resolved.length) return;

      const missingSize = resolved.some(function (entry) {
        return entry && entry.reason === 'select';
      });
      const allAvailable = resolved.every(function (entry) {
        return entry && entry.available && entry.id;
      });
      const variantIds = resolved.map(function (entry) {
        return entry && entry.id ? entry.id : '';
      });

      button.setAttribute('data-variant-ids', variantIds.join(','));

      if (allAvailable) {
        button.setAttribute('data-available', 'true');
        button.disabled = false;
        button.textContent = labelAdd;
        if (messageEl) {
          messageEl.hidden = true;
          messageEl.textContent = labelSoldOut;
        }
        return;
      }

      button.setAttribute('data-available', 'false');
      button.disabled = true;
      if (missingSize) {
        button.textContent = labelSelect;
        if (messageEl) {
          messageEl.hidden = false;
          messageEl.textContent = labelSelect;
        }
      } else {
        button.textContent = labelAdd;
        if (messageEl) {
          messageEl.hidden = false;
          messageEl.textContent = labelSoldOut;
        }
      }
    };

    buttons.forEach(function (button) {
      button.setAttribute('data-onera-look-add-init', 'true');
      const panel = button.closest('[data-look-panel]');
      if (!panel) return;

      syncPanel(panel, button);

      panel.querySelectorAll('[data-look-size-choice]').forEach(function (input) {
        input.addEventListener('change', function () {
          syncPanel(panel, button);
        });
      });

      button.addEventListener('click', function () {
        const api = window.oneraCartCore;
        syncPanel(panel, button);
        const rawIds = button.getAttribute('data-variant-ids');
        if (!rawIds || !api) return;
        if (button.getAttribute('data-available') === 'false') return;

        const variantIds = rawIds
          .split(',')
          .map(function (id) {
            return parseInt(id.trim(), 10);
          })
          .filter(function (id) {
            return !isNaN(id);
          });

        if (!variantIds.length) return;

        const productIds = parseCsv(button.getAttribute('data-product-ids'));
        const productHandles = parseCsv(button.getAttribute('data-product-handles'));
        const setRoles = parseCsv(button.getAttribute('data-set-roles'));

        let meta = null;
        if (productIds.length === variantIds.length && productIds.length >= 2) {
          meta = {
            lookBundle: true,
            productIds: productIds,
            productHandles: productHandles.length === productIds.length ? productHandles : [],
            setRoles: setRoles.length === productIds.length ? setRoles : []
          };
        }

        button.disabled = true;

        api
          .addItems(
            variantIds.map(function (id) {
              return { id: id, quantity: 1 };
            }),
            'homepage-get-the-look',
            meta
          )
          .catch(function (error) {
            api.showError(
              button.closest('[data-onera-look]') || button.parentElement,
              api.getErrorMessage(error, 'add')
            );
          })
          .finally(function () {
            syncPanel(panel, button);
          });
      });
    });
  };

  const initGetTheLookSwitcher = function (scope) {
    const roots = queryInScope('[data-onera-look-switcher]:not([data-onera-look-switcher-init])', scope);
    if (!roots.length) return;

    roots.forEach(function (root) {
      root.setAttribute('data-onera-look-switcher-init', 'true');
      const sectionId = root.getAttribute('data-onera-look-id');
      const buttons = root.querySelectorAll('[data-onera-look-colour]');
      const panels = root.querySelectorAll('[data-look-panel]');
      if (!buttons.length || !panels.length) return;

      const activateLook = function (lookId) {
        if (lookId !== 'ivory' && lookId !== 'black') return;

        const blackPanel = root.querySelector('[data-look-panel="black"]');
        if (lookId === 'black' && !blackPanel) return;

        const targetButton = root.querySelector(
          '[data-onera-look-colour="' + lookId + '"]'
        );
        if (targetButton && targetButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        panels.forEach(function (panel) {
          const isActive = panel.getAttribute('data-look-panel') === lookId;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        });

        buttons.forEach(function (button) {
          const isPressed = button.getAttribute('data-onera-look-colour') === lookId;
          button.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
        });

        if (sectionId) {
          try {
            sessionStorage.setItem('onera:look-colour:' + sectionId, lookId);
          } catch (error) {
            /* sessionStorage unavailable */
          }
        }
      };

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          if (button.getAttribute('aria-disabled') === 'true') return;
          activateLook(button.getAttribute('data-onera-look-colour'));
        });
      });

      const switcher = root.querySelector('.onera-look__colour-switch');
      if (switcher) {
        switcher.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

          const enabledButtons = Array.prototype.filter.call(buttons, function (button) {
            return button.getAttribute('aria-disabled') !== 'true';
          });

          if (enabledButtons.length < 2) return;

          const currentIndex = enabledButtons.findIndex(function (button) {
            return button.getAttribute('aria-pressed') === 'true';
          });

          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % enabledButtons.length;
          } else {
            nextIndex = (currentIndex - 1 + enabledButtons.length) % enabledButtons.length;
          }

          event.preventDefault();
          const nextButton = enabledButtons[nextIndex];
          activateLook(nextButton.getAttribute('data-onera-look-colour'));
          nextButton.focus();
        });
      }

      let storedLook = null;
      if (sectionId) {
        try {
          storedLook = sessionStorage.getItem('onera:look-colour:' + sectionId);
        } catch (error) {
          storedLook = null;
        }
      }

      const fallback = root.getAttribute('data-onera-look-default') || 'ivory';
      const initialLook =
        storedLook === 'black' || storedLook === 'ivory' ? storedLook : fallback;

      if (
        !isDesignMode() &&
        initialLook === 'black' &&
        root.querySelector('[data-look-panel="black"]')
      ) {
        activateLook('black');
      }
    });
  };

  /* Product page: keep the styled option controls in sync with the native
     variant <select> (the functional source of truth), and reflect the
     resolved variant's price + availability. Deliberately minimal — gallery
     swipe/zoom, media switching, and CRO modules belong to later phases. */
  const initProductForm = function () {
    const root = document.querySelector('[data-pdp]');
    if (!root) return;

    const dataEl = root.querySelector('[data-pdp-variants]');
    const select = root.querySelector('[data-pdp-select]');
    if (!dataEl || !select) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!variants.length) return;

    const options = Array.prototype.slice.call(
      root.querySelectorAll('[data-pdp-option]')
    );

    const motionReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    const focusOptionsIfNeeded = function () {
      if (!options.length) return true;
      let incomplete = null;
      options.forEach(function (fieldset) {
        if (!fieldset.querySelector('[data-pdp-choice]:checked')) {
          if (!incomplete) incomplete = fieldset;
        }
      });
      if (!incomplete) return true;
      incomplete.classList.add('is-option-required');
      incomplete.scrollIntoView({
        behavior: motionReduce.matches ? 'auto' : 'smooth',
        block: 'center'
      });
      const firstInput = incomplete.querySelector('[data-pdp-choice]');
      if (firstInput && typeof firstInput.focus === 'function') {
        firstInput.focus();
      }
      window.setTimeout(function () {
        incomplete.classList.remove('is-option-required');
      }, 2400);
      return false;
    };

    const priceEl = root.querySelector('[data-pdp-price]');
    const availabilityEl = root.querySelector('[data-pdp-availability]');
    const addBtn = root.querySelector('[data-pdp-add]');
    const addLabel = root.querySelector('[data-pdp-add-label]');
    const form = root.querySelector('#onera-pdp-form');
    const purchaseHost = root.querySelector('[data-pdp-purchase]') || form;

    // Sticky mobile purchase bar — a display mirror, not a second form.
    const sticky = root.querySelector('[data-pdp-sticky]');
    const stickyPrice = root.querySelector('[data-pdp-sticky-price]');
    const stickyMeta = root.querySelector('[data-pdp-sticky-meta]');
    const stickyAdd = root.querySelector('[data-pdp-sticky-add]');
    const stickyAddLabel = root.querySelector('[data-pdp-sticky-add-label]');
    const stickyStatus = root.querySelector('[data-pdp-sticky-status]');
    const stickyLabel = function (name, fallback) {
      return stickyAdd && stickyAdd.getAttribute('data-label-' + name)
        ? stickyAdd.getAttribute('data-label-' + name)
        : fallback;
    };

    const label = function (name, fallback) {
      return addBtn && addBtn.getAttribute('data-label-' + name)
        ? addBtn.getAttribute('data-label-' + name)
        : fallback;
    };

    const getPromoMeta = function () {
      const el = document.querySelector('[data-pdp-promo-context]');
      if (!el) return null;
      try {
        const data = JSON.parse(el.textContent);
        if (!data || !data.productId) return null;
        return {
          productId: data.productId,
          matchProductId: data.matchingProductId,
          matchHandle: data.matchingProductHandle,
          productHandle: data.productHandle,
          setRole: data.setRole || null
        };
      } catch (error) {
        return null;
      }
    };

    const getSelectedValues = function () {
      return options.map(function (fieldset) {
        const checked = fieldset.querySelector('[data-pdp-choice]:checked');
        return checked ? checked.value : null;
      });
    };

    const findVariant = function (values) {
      return variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          return option === values[index];
        });
      });
    };

    // Match a variant that keeps the current picks but swaps one option value —
    // used to grey out sizes that are sold out for the chosen colour.
    const findVariantForChoice = function (optionIndex, value, values) {
      return variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          if (index === optionIndex) return option === value;
          if (values[index] == null) return true;
          return option === values[index];
        });
      });
    };

    const renderPrice = function (variant) {
      if (!priceEl) return;
      if (variant.on_sale) {
        priceEl.innerHTML =
          '<span class="onera-pdp__price-current">' +
          variant.price_money +
          '</span><s class="onera-pdp__price-was">' +
          variant.compare_money +
          '</s>';
      } else {
        priceEl.innerHTML =
          '<span class="onera-pdp__price-current">' +
          variant.price_money +
          '</span>';
      }
    };

    const renderAvailability = function (variant) {
      if (addBtn) {
        if (variant && variant.available) {
          addBtn.disabled = false;
          if (addLabel) addLabel.textContent = label('add', 'Add to bag');
        } else {
          addBtn.disabled = true;
          if (addLabel) addLabel.textContent = label('sold-out', 'Sold out');
        }
      }

      if (!availabilityEl) return;
      availabilityEl.classList.remove('is-in', 'is-low', 'is-out');
      if (!variant || !variant.available) {
        availabilityEl.textContent =
          availabilityEl.getAttribute('data-label-out') || 'Sold out';
        availabilityEl.classList.add('is-out');
        return;
      }
      const threshold =
        parseInt(availabilityEl.getAttribute('data-low-threshold'), 10) || 0;
      const qty = variant.inventory_quantity;
      if (
        variant.inventory_management &&
        threshold > 0 &&
        qty > 0 &&
        qty <= threshold
      ) {
        const template =
          availabilityEl.getAttribute('data-label-low') || 'Only __n__ left';
        availabilityEl.textContent = template.replace('__n__', qty);
        availabilityEl.classList.add('is-low');
      } else {
        availabilityEl.textContent =
          availabilityEl.getAttribute('data-label-in') || 'In stock';
        availabilityEl.classList.add('is-in');
      }
    };

    const updateSelectedLabels = function () {
      options.forEach(function (fieldset) {
        const checked = fieldset.querySelector('[data-pdp-choice]:checked');
        const selectedEl = fieldset.querySelector('[data-pdp-option-selected]');
        if (checked && selectedEl) {
          selectedEl.textContent = checked.value;
        }
      });
    };

    const updateOptionAvailability = function (values) {
      options.forEach(function (fieldset) {
        const optionIndex =
          parseInt(fieldset.getAttribute('data-option-position'), 10) - 1;
        fieldset
          .querySelectorAll('[data-pdp-choice]')
          .forEach(function (input) {
            const variant = findVariantForChoice(
              optionIndex,
              input.value,
              values
            );
            const unavailable = !variant || !variant.available;
            const choiceLabel = fieldset.querySelector(
              'label[for="' + input.id + '"]'
            );
            if (choiceLabel) {
              choiceLabel.classList.toggle('is-unavailable', unavailable);
            }
          });
      });
    };

    // Mirror the resolved state onto the sticky bar (display only).
    const updateSticky = function (variant) {
      if (!sticky) return;
      const parts = [];
      options.forEach(function (fieldset) {
        const checked = fieldset.querySelector('[data-pdp-choice]:checked');
        if (checked) parts.push(checked.value);
      });
      if (stickyMeta) stickyMeta.textContent = parts.join(' · ');
      if (stickyPrice && variant) stickyPrice.textContent = variant.price_money;

      const sizeSelect = sticky.querySelector('[data-pdp-sticky-size-select]');
      if (sizeSelect) {
        const sizePos = sizeSelect.getAttribute('data-option-position');
        let sizeValue = '';
        options.forEach(function (fieldset) {
          if (String(fieldset.getAttribute('data-option-position')) !== String(sizePos)) return;
          const checked = fieldset.querySelector('[data-pdp-choice]:checked');
          if (checked) sizeValue = checked.value;
        });
        if (sizeValue) sizeSelect.value = sizeValue;
      }

      sticky.querySelectorAll('[data-pdp-sticky-size]').forEach(function (btn) {
        btn.classList.toggle('is-active', parts.indexOf(btn.getAttribute('data-option-value')) !== -1);
      });

      if (!stickyAdd) return;
      if (variant && variant.available) {
        stickyAdd.disabled = false;
        if (stickyAddLabel) {
          stickyAddLabel.textContent = stickyLabel('add', 'Add to bag');
        }
      } else {
        stickyAdd.disabled = true;
        if (stickyAddLabel) {
          stickyAddLabel.textContent = stickyLabel('sold-out', 'Sold out');
        }
      }
    };

    const applyStickySizeValue = function (value) {
      if (!value) return;
      let matched = null;
      root.querySelectorAll('[data-pdp-choice]').forEach(function (choice) {
        if (choice.value === value) matched = choice;
      });
      if (!matched) return;
      matched.checked = true;
      matched.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const onChange = function () {
      updateSelectedLabels();
      const values = getSelectedValues();
      updateOptionAvailability(values);
      const variant = findVariant(values);
      if (!variant) {
        renderAvailability(null);
        updateSticky(null);
        return;
      }
      select.value = variant.id;
      renderPrice(variant);
      renderAvailability(variant);
      updateSticky(variant);
    };

    if (options.length) {
      root.querySelectorAll('[data-pdp-choice]').forEach(function (input) {
        input.addEventListener('change', onChange);
      });
    }

    // Quantity stepper — minimal, keyboard + button controlled.
    const qtyInput = root.querySelector('[data-pdp-qty]');
    const qtyDown = root.querySelector('[data-pdp-qty-down]');
    const qtyUp = root.querySelector('[data-pdp-qty-up]');
    const setQty = function (value) {
      if (qtyInput) qtyInput.value = Math.max(1, value || 1);
    };
    if (qtyInput && qtyDown) {
      qtyDown.addEventListener('click', function () {
        setQty(parseInt(qtyInput.value, 10) - 1);
      });
    }
    if (qtyInput && qtyUp) {
      qtyUp.addEventListener('click', function () {
        setQty(parseInt(qtyInput.value, 10) + 1);
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        setQty(parseInt(qtyInput.value, 10));
      });
    }

    // AJAX add to bag — opens the luxury cart drawer on success.
    if (form && addBtn) {
      let pending = false;

      const resetAdd = function () {
        addBtn.classList.remove('is-loading', 'is-added');
        if (addLabel) addLabel.textContent = label('add', 'Add to bag');
        pending = false;
      };

      form.addEventListener('submit', function (event) {
        if (addBtn.disabled || pending) return;
        event.preventDefault();

        if (!focusOptionsIfNeeded()) return;

        const id = select.value;
        if (!id) return;
        const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        pending = true;
        addBtn.classList.add('is-loading');
        if (addLabel) addLabel.textContent = label('loading', 'Adding…');

        const api = window.oneraCartCore;
        if (!api) {
          resetAdd();
          return;
        }

        api.clearError(purchaseHost);
        api
          .addItems([{ id: Number(id), quantity: quantity }], 'pdp-main', getPromoMeta())
          .then(function () {
            addBtn.classList.remove('is-loading');
            addBtn.classList.add('is-added');
            if (addLabel) addLabel.textContent = label('added', 'Added');
            window.setTimeout(resetAdd, 1800);
          })
          .catch(function (error) {
            api.showError(purchaseHost, api.getErrorMessage(error, 'add'));
            resetAdd();
          });
      });
    }

    // Initial sync: availability + sold-out option states (price is
    // already server-rendered, so no flash / layout shift).
    const initialValues = getSelectedValues();
    if (options.length) updateOptionAvailability(initialValues);
    const initialVariant = options.length
      ? findVariant(initialValues)
      : variants[0];
    if (initialVariant) {
      renderAvailability(initialVariant);
      updateSticky(initialVariant);
    }

    /* Sticky bar: show only when the main Add to Bag is out of view; hide while cart drawer is open. */
    if (sticky && stickyAdd && addBtn) {
      let drawerOpen = false;
      let mainAddVisible = true;
      const stickySizes = sticky.querySelector('[data-pdp-sticky-sizes]');
      const stickySizeSelect = sticky.querySelector('[data-pdp-sticky-size-select]');

      const setSticky = function (visible) {
        const show = visible && !drawerOpen;
        sticky.classList.toggle('is-visible', show);
        sticky.classList.toggle('is-drawer-open', drawerOpen);
        root.classList.toggle('has-sticky-visible', show);
        if (show) {
          sticky.removeAttribute('inert');
        } else {
          sticky.setAttribute('inert', '');
        }
      };

      const refreshStickyVisibility = function () {
        setSticky(!mainAddVisible);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              mainAddVisible = entry.isIntersecting;
              refreshStickyVisibility();
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );
        observer.observe(addBtn);
        const rect = addBtn.getBoundingClientRect();
        mainAddVisible = rect.top < window.innerHeight && rect.bottom > 0;
        refreshStickyVisibility();
      } else {
        setSticky(true);
      }

      document.addEventListener('onera:cart:open', function () {
        drawerOpen = true;
        refreshStickyVisibility();
      });

      document.addEventListener('onera:cart:close', function () {
        drawerOpen = false;
        refreshStickyVisibility();
      });

      if (stickySizeSelect) {
        stickySizeSelect.addEventListener('change', function () {
          applyStickySizeValue(stickySizeSelect.value);
        });
      }

      if (stickySizes) {
        sticky.querySelectorAll('[data-pdp-sticky-size]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            applyStickySizeValue(btn.getAttribute('data-option-value'));
          });
        });
      }

      let pending = false;
      const resetSticky = function () {
        stickyAdd.classList.remove('is-loading', 'is-added');
        if (stickyAddLabel) {
          stickyAddLabel.textContent = stickyLabel('add', 'Add to bag');
        }
        pending = false;
      };

      stickyAdd.addEventListener('click', function () {
        if (pending || stickyAdd.disabled) return;
        if (!focusOptionsIfNeeded()) {
          if (stickySizeSelect && window.matchMedia('(max-width: 767px)').matches) {
            stickySizeSelect.focus();
          }
          return;
        }
        const id = select.value;
        if (!id) return;
        const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        pending = true;
        stickyAdd.classList.add('is-loading');
        if (stickyAddLabel) {
          stickyAddLabel.textContent = stickyLabel('loading', 'Adding…');
        }

        const api = window.oneraCartCore;
        if (!api) {
          resetSticky();
          return;
        }

        api.clearError(sticky);
        api
          .addItems([{ id: Number(id), quantity: quantity }], 'pdp-sticky', getPromoMeta())
          .then(function () {
            stickyAdd.classList.remove('is-loading');
            stickyAdd.classList.add('is-added');
            if (stickyAddLabel) {
              stickyAddLabel.textContent = stickyLabel('added', 'Added');
            }
            if (stickyStatus) {
              stickyStatus.textContent = stickyLabel('added', 'Added');
            }
            window.setTimeout(resetSticky, 1800);
          })
          .catch(function (error) {
            var message = api.getErrorMessage(error, 'add');
            api.showError(sticky, message);
            if (stickyStatus) stickyStatus.textContent = message;
            resetSticky();
          });
      });
    }
  };

  /* Product gallery: mobile scroll-snap swipe, desktop thumbnail lookbook
     with cross-fade, minimal indicators, variant-image sync, and a
     chrome-free fullscreen lightbox (swipe + pinch/double-tap zoom, focus
     trap, scroll lock). Vanilla, no libraries. Gallery only — nothing here
     touches the purchase panel. */
  const clamp = function (value, min, max) {
    return Math.max(min, Math.min(max, value));
  };

  const buildLightbox = function (onClose) {
    const lightbox = document.querySelector('[data-pdp-lightbox]');
    if (!lightbox) return null;

    const lbTrack = lightbox.querySelector('[data-pdp-lightbox-track]');
    const slides = Array.prototype.slice.call(
      lightbox.querySelectorAll('[data-pdp-lightbox-slide]')
    );
    if (!lbTrack || !slides.length) return null;

    const closeBtn = lightbox.querySelector('[data-pdp-lightbox-close]');
    const prevBtn = lightbox.querySelector('[data-pdp-lightbox-prev]');
    const nextBtn = lightbox.querySelector('[data-pdp-lightbox-next]');
    const counter = lightbox.querySelector('[data-pdp-lightbox-counter]');
    const focusable = [closeBtn, prevBtn, nextBtn].filter(Boolean);

    let index = 0;
    let lastFocused = null;
    let scale = 1;
    let tx = 0;
    let ty = 0;

    const pointers = new Map();
    let startDist = 0;
    let startScale = 1;
    let panStartX = 0;
    let panStartY = 0;
    let startTx = 0;
    let startTy = 0;
    let lastTap = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    const currentScaler = function () {
      return slides[index]
        ? slides[index].querySelector('[data-pdp-lightbox-scaler]')
        : null;
    };

    const applyTransform = function () {
      const scaler = currentScaler();
      if (!scaler) return;
      scaler.style.transform =
        'translate3d(' + tx + 'px,' + ty + 'px,0) scale(' + scale + ')';
    };

    const resetZoom = function () {
      scale = 1;
      tx = 0;
      ty = 0;
      const scaler = currentScaler();
      if (scaler) scaler.style.transform = '';
      lightbox.classList.remove('is-zoomed');
    };

    const constrainPan = function () {
      const scaler = currentScaler();
      if (!scaler) return;
      const img = scaler.querySelector('img');
      if (!img) return;
      const rect = lbTrack.getBoundingClientRect();
      const maxX = Math.max(0, (img.clientWidth * scale - rect.width) / 2);
      const maxY = Math.max(0, (img.clientHeight * scale - rect.height) / 2);
      tx = clamp(tx, -maxX, maxX);
      ty = clamp(ty, -maxY, maxY);
    };

    const updateCounter = function () {
      if (counter) counter.textContent = index + 1 + ' / ' + slides.length;
    };

    const goTo = function (target, instant) {
      index = clamp(target, 0, slides.length - 1);
      resetZoom();
      if (slides[index]) {
        lbTrack.scrollTo({
          left: slides[index].offsetLeft,
          behavior: instant ? 'auto' : 'smooth'
        });
      }
      updateCounter();
    };

    const toggleZoom = function (cx, cy) {
      if (scale > 1.01) {
        resetZoom();
        return;
      }
      scale = 2.4;
      const rect = lbTrack.getBoundingClientRect();
      const offsetX = cx - rect.left - rect.width / 2;
      const offsetY = cy - rect.top - rect.height / 2;
      tx = -offsetX * (scale - 1);
      ty = -offsetY * (scale - 1);
      lightbox.classList.add('is-zoomed');
      constrainPan();
      applyTransform();
    };

    const dist = function (a, b) {
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    lbTrack.addEventListener('pointerdown', function (event) {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const pts = Array.prototype.slice.call(pointers.values());
        startDist = dist(pts[0], pts[1]);
        startScale = scale;
      } else if (pointers.size === 1) {
        panStartX = event.clientX;
        panStartY = event.clientY;
        startTx = tx;
        startTy = ty;
      }
    });

    lbTrack.addEventListener(
      'pointermove',
      function (event) {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (pointers.size === 2 && startDist > 0) {
          const pts = Array.prototype.slice.call(pointers.values());
          scale = clamp((startScale * dist(pts[0], pts[1])) / startDist, 1, 4);
          lightbox.classList.toggle('is-zoomed', scale > 1.01);
          constrainPan();
          applyTransform();
          event.preventDefault();
        } else if (pointers.size === 1 && scale > 1.01) {
          tx = startTx + (event.clientX - panStartX);
          ty = startTy + (event.clientY - panStartY);
          constrainPan();
          applyTransform();
          event.preventDefault();
        }
      },
      { passive: false }
    );

    const endPointer = function (event) {
      pointers.delete(event.pointerId);
      if (pointers.size === 0) {
        if (scale <= 1.01) resetZoom();
      } else if (pointers.size === 1) {
        const remaining = Array.prototype.slice.call(pointers.values())[0];
        panStartX = remaining.x;
        panStartY = remaining.y;
        startTx = tx;
        startTy = ty;
      }
    };

    lbTrack.addEventListener('pointerup', function (event) {
      const onImage = event.target.closest('[data-pdp-lightbox-scaler]');
      if (onImage) {
        const now = Date.now();
        if (
          now - lastTap < 300 &&
          Math.abs(event.clientX - lastTapX) < 30 &&
          Math.abs(event.clientY - lastTapY) < 30
        ) {
          toggleZoom(event.clientX, event.clientY);
          lastTap = 0;
        } else {
          lastTap = now;
          lastTapX = event.clientX;
          lastTapY = event.clientY;
        }
      } else if (scale <= 1.01) {
        close();
      }
      endPointer(event);
    });

    lbTrack.addEventListener('pointercancel', endPointer);

    lbTrack.addEventListener(
      'scroll',
      function () {
        if (lightbox.classList.contains('is-zoomed')) return;
        const width = lbTrack.clientWidth;
        if (!width) return;
        const next = Math.round(lbTrack.scrollLeft / width);
        if (next !== index) {
          index = next;
          resetZoom();
          updateCounter();
        }
      },
      { passive: true }
    );

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
      });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        close();
      });
    }

    lightbox.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowRight') {
        goTo(index + 1);
      } else if (event.key === 'ArrowLeft') {
        goTo(index - 1);
      } else if (event.key === 'Tab' && focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    function open(target) {
      lastFocused = document.activeElement;
      index = clamp(target, 0, slides.length - 1);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('onera-no-scroll');
      updateCounter();
      window.requestAnimationFrame(function () {
        goTo(index, true);
      });
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      resetZoom();
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('onera-no-scroll');
      if (typeof onClose === 'function') onClose(index);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    return { open: open };
  };

  const initProductGallery = function () {
    const gallery = document.querySelector('[data-pdp-gallery]');
    if (!gallery) return;

    const track = gallery.querySelector('[data-pdp-gallery-viewport]');
    const mediaEls = Array.prototype.slice.call(
      gallery.querySelectorAll('[data-pdp-media]')
    );
    if (!track || !mediaEls.length) return;

    const thumbs = Array.prototype.slice.call(
      gallery.querySelectorAll('[data-pdp-thumb]')
    );
    const indicators = Array.prototype.slice.call(
      gallery.querySelectorAll('[data-pdp-indicators] [data-pdp-goto]')
    );
    const stage = gallery.querySelector('[data-pdp-stage]');
    const status = gallery.querySelector('[data-pdp-status]');
    const desktopQuery = window.matchMedia('(min-width: 1024px)');
    let current = 0;

    const setActive = function (target, opts) {
      current = clamp(target, 0, mediaEls.length - 1);
      const options = opts || {};

      mediaEls.forEach(function (el, idx) {
        const on = idx === current;
        el.classList.toggle('is-active', on);
        const trigger = el.querySelector('[data-pdp-open]');
        if (trigger) trigger.setAttribute('tabindex', on ? '0' : '-1');
      });
      thumbs.forEach(function (thumb, idx) {
        const on = idx === current;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      indicators.forEach(function (dot, idx) {
        dot.classList.toggle('is-active', idx === current);
      });
      if (status) {
        status.textContent =
          'Image ' + (current + 1) + ' of ' + mediaEls.length;
      }
      if (options.scroll && !desktopQuery.matches) {
        const targetEl = mediaEls[current];
        if (targetEl) {
          track.scrollTo({
            left: targetEl.offsetLeft,
            behavior: options.instant ? 'auto' : 'smooth'
          });
        }
      }
    };

    // Mobile: reflect the swiped-to image in the indicators/status.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        function (entries) {
          if (desktopQuery.matches) return;
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              const idx = mediaEls.indexOf(entry.target);
              if (idx > -1 && idx !== current) setActive(idx);
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      mediaEls.forEach(function (el) {
        observer.observe(el);
      });
    }

    // Desktop: thumbnails drive the cross-fade.
    thumbs.forEach(function (thumb, idx) {
      thumb.addEventListener('click', function () {
        setActive(idx);
      });
      thumb.addEventListener('mouseenter', function () {
        if (desktopQuery.matches) setActive(idx);
      });
      thumb.addEventListener('focus', function () {
        if (desktopQuery.matches) setActive(idx);
      });
    });

    indicators.forEach(function (dot, idx) {
      dot.addEventListener('click', function () {
        setActive(idx, { scroll: true });
      });
    });

    if (stage) {
      stage.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          setActive(current + 1, { scroll: true });
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setActive(current - 1, { scroll: true });
        }
      });
    }

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', function () {
        setActive(current, { scroll: true, instant: true });
      });
    }

    // Fullscreen lightbox — sync the main gallery to the last viewed image.
    const lightbox = buildLightbox(function (lastIndex) {
      setActive(lastIndex, { scroll: true, instant: true });
    });

    mediaEls.forEach(function (el, idx) {
      const trigger = el.querySelector('[data-pdp-open]');
      if (trigger && lightbox) {
        trigger.addEventListener('click', function () {
          lightbox.open(idx);
        });
      }
    });

    // Variant colour -> primary image sync (uses Section 1 variant data).
    const dataEl = gallery
      .closest('[data-pdp]')
      ? gallery.closest('[data-pdp]').querySelector('[data-pdp-variants]')
      : null;
    const optionFieldsets = Array.prototype.slice
      .call(document.querySelectorAll('[data-pdp-option]'))
      .sort(function (a, b) {
        return (
          parseInt(a.getAttribute('data-option-position'), 10) -
          parseInt(b.getAttribute('data-option-position'), 10)
        );
      });

    if (dataEl && optionFieldsets.length) {
      let variants = [];
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        variants = [];
      }

      const mediaIndexById = {};
      mediaEls.forEach(function (el, idx) {
        mediaIndexById[el.getAttribute('data-media-id')] = idx;
      });

      const resolveVariant = function () {
        const values = optionFieldsets.map(function (fieldset) {
          const checked = fieldset.querySelector('[data-pdp-choice]:checked');
          return checked ? checked.value : null;
        });
        return variants.find(function (variant) {
          return variant.options.every(function (option, i) {
            return option === values[i];
          });
        });
      };

      document
        .querySelectorAll('[data-pdp-choice]')
        .forEach(function (input) {
          input.addEventListener('change', function () {
            const variant = resolveVariant();
            if (variant && variant.featured_media_id != null) {
              const idx = mediaIndexById[String(variant.featured_media_id)];
              if (idx != null) setActive(idx, { scroll: true });
            }
          });
        });
    }
  };

  /* Product details: editorial accordions. Single-open, remembers the open
     row per session, animates height with the CSS grid trick, and keeps ARIA
     + [hidden] correct for keyboard / screen readers. No libraries. */
  const initAccordion = function () {
    const root = document.querySelector('[data-pdp-accordion]');
    if (!root) return;

    const STORAGE_KEY = 'onera_pdp_accordion';
    const triggers = Array.prototype.slice.call(
      root.querySelectorAll('[data-accordion-trigger]')
    );
    if (!triggers.length) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sideQuery = window.matchMedia('(min-width: 768px)');
    const faqShell = root.closest('[data-pdp-faq]');
    const sidePane = faqShell ? faqShell.querySelector('[data-faq-side]') : null;
    const sideBody = faqShell ? faqShell.querySelector('[data-faq-side-body]') : null;
    const sideQuestion = faqShell
      ? faqShell.querySelector('[data-faq-side-question]')
      : null;
    const sidePlaceholder = faqShell
      ? faqShell.querySelector('.onera-pdp__faq-side-placeholder')
      : null;
    const DURATION = 250;

    const panelFor = function (trigger) {
      return document.getElementById(trigger.getAttribute('aria-controls'));
    };

    const remember = function (key) {
      try {
        if (key) sessionStorage.setItem(STORAGE_KEY, key);
        else sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* sessionStorage unavailable */
      }
    };

    const syncSidePane = function (trigger) {
      if (!sidePane || !sideBody) return;
      if (!sideQuery.matches || !trigger) {
        sideBody.hidden = true;
        sideBody.innerHTML = '';
        if (sideQuestion) {
          sideQuestion.hidden = true;
          sideQuestion.textContent = '';
        }
        if (sidePlaceholder) sidePlaceholder.hidden = false;
        return;
      }
      const panel = panelFor(trigger);
      const content = panel && panel.querySelector('.onera-accordion__content');
      const titleEl = trigger.querySelector('.onera-accordion__title');
      if (!content) {
        sideBody.hidden = true;
        sideBody.innerHTML = '';
        if (sideQuestion) {
          sideQuestion.hidden = true;
          sideQuestion.textContent = '';
        }
        if (sidePlaceholder) sidePlaceholder.hidden = false;
        return;
      }
      if (sideQuestion) {
        sideQuestion.textContent = titleEl
          ? String(titleEl.textContent || '').trim()
          : '';
        sideQuestion.hidden = !sideQuestion.textContent;
      }
      sideBody.innerHTML = content.innerHTML;
      sideBody.hidden = false;
      if (sidePlaceholder) sidePlaceholder.hidden = true;
    };

    const openRow = function (trigger, opts) {
      opts = opts || {};
      const panel = panelFor(trigger);
      if (!panel) return;
      if (panel.dataset.timer) {
        clearTimeout(Number(panel.dataset.timer));
        delete panel.dataset.timer;
      }
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      if (opts.animate === false || reduce.matches) {
        panel.classList.add('is-open');
      } else {
        // Force a reflow so the grid change animates from 0fr.
        void panel.offsetHeight;
        panel.classList.add('is-open');
      }
      if (opts.store !== false) remember(trigger.dataset.accordionKey);
      syncSidePane(trigger);
    };

    const closeRow = function (trigger, opts) {
      opts = opts || {};
      const panel = panelFor(trigger);
      if (!panel) return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      if (panel.dataset.timer) {
        clearTimeout(Number(panel.dataset.timer));
        delete panel.dataset.timer;
      }
      if (opts.animate === false || reduce.matches) {
        panel.hidden = true;
      } else {
        panel.dataset.timer = String(
          window.setTimeout(function () {
            panel.hidden = true;
            delete panel.dataset.timer;
          }, DURATION)
        );
      }
      if (opts.clearSide !== false) syncSidePane(null);
    };

    const toggle = function (trigger) {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeRow(trigger);
        remember(null);
        return;
      }
      // Single-open: gracefully close whichever row is currently open.
      triggers.forEach(function (other) {
        if (other !== trigger && other.getAttribute('aria-expanded') === 'true') {
          closeRow(other, { clearSide: false });
        }
      });
      openRow(trigger);
    };

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        toggle(trigger);
      });
    });

    if (sideQuery.addEventListener) {
      sideQuery.addEventListener('change', function () {
        const open = triggers.find(function (trigger) {
          return trigger.getAttribute('aria-expanded') === 'true';
        });
        syncSidePane(open || null);
      });
    }

    // Restore the remembered row (instantly, no animation on load).
    let stored = null;
    try {
      stored = sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      stored = null;
    }
    if (stored) {
      const match = triggers.find(function (trigger) {
        return trigger.dataset.accordionKey === stored;
      });
      if (match) openRow(match, { animate: false, store: false });
    }
  };

  /* Fit & Sizing modal: full-screen on mobile, centred on desktop. Focus
     trap, Escape + backdrop close, scroll lock, reduced-motion aware. Reuses
     the shared html.onera-no-scroll lock. No libraries. */
  const initSizeGuide = function () {
    const modal = document.querySelector('[data-sizeguide]');
    if (!modal) return;

    const openers = Array.prototype.slice.call(
      document.querySelectorAll('[data-sizeguide-open]')
    );
    if (!openers.length) return;

    const closers = Array.prototype.slice.call(
      modal.querySelectorAll('[data-sizeguide-close]')
    );
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let lastFocused = null;
    let closeTimer = null;

    const focusables = function () {
      return Array.prototype.slice
        .call(
          modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        });
    };

    const open = function () {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('onera-no-scroll');
      window.requestAnimationFrame(function () {
        modal.classList.add('is-visible');
      });
      const closeBtn = modal.querySelector('.onera-sizeguide__close');
      if (closeBtn) closeBtn.focus();
    };

    const close = function () {
      modal.classList.remove('is-visible');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('onera-no-scroll');
      const finish = function () {
        modal.classList.remove('is-open');
        closeTimer = null;
      };
      if (reduce.matches) {
        finish();
      } else {
        closeTimer = window.setTimeout(finish, 250);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    };

    openers.forEach(function (opener) {
      opener.addEventListener('click', open);
    });
    closers.forEach(function (closer) {
      closer.addEventListener('click', close);
    });

    modal.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  /* Complete The Look: AJAX add via oneraCartCore. Preserves the current page,
     and matches size/colour from the main product selection when possible. */
  const initCompleteLook = function () {
    const root = document.querySelector('[data-complete-look]');
    if (!root) return;

    const addBtn = root.querySelector('[data-complete-look-add]');
    const label = root.querySelector('[data-complete-look-add-label]');
    const status = root.querySelector('[data-complete-look-status]');
    const dataEl = root.querySelector('[data-complete-look-variants]');
    if (!addBtn || !dataEl) return;

    let variants = [];
    let mainVariants = [];
    let optionNames = [];
    try {
      const payload = JSON.parse(dataEl.textContent) || [];
      if (Array.isArray(payload)) {
        variants = payload;
      } else {
        variants = payload.variants || [];
        mainVariants = payload.main_variants || [];
        optionNames = payload.option_names || [];
      }
    } catch (error) {
      variants = [];
      mainVariants = [];
      optionNames = [];
    }

    const text = function (key, fallback) {
      return addBtn.getAttribute('data-label-' + key) || fallback;
    };

    const normalizeOptionKey = function (name) {
      const key = String(name || '')
        .trim()
        .toLowerCase();
      if (key === 'colour') return 'color';
      return key;
    };

    const getPdpOptionLabels = function () {
      return Array.prototype.slice
        .call(document.querySelectorAll('[data-pdp-option] .onera-pdp__field-label'))
        .map(function (el) {
          return String(el.textContent || '').trim();
        })
        .filter(Boolean);
    };

    /* Read size/colour (and any other options) from the main PDP selectors. */
    const getPdpSelections = function () {
      const selections = {};
      document.querySelectorAll('[data-pdp-option]').forEach(function (fieldset) {
        const nameEl = fieldset.querySelector('.onera-pdp__field-label');
        const checked = fieldset.querySelector('[data-pdp-choice]:checked');
        if (!nameEl || !checked) return;
        selections[normalizeOptionKey(nameEl.textContent)] = String(checked.value || '').trim();
      });
      return selections;
    };

    const getCartSelections = function (cart) {
      const productId = Number(addBtn.getAttribute('data-main-product-id')) || null;
      if (!productId || !cart || !cart.items || !cart.items.length) return null;

      const lines = cart.items.filter(function (item) {
        return Number(item.product_id) === productId;
      });
      if (!lines.length) return null;

      const line = lines[lines.length - 1];
      const labels = getPdpOptionLabels();
      const selections = {};
      const values = [line.option1, line.option2, line.option3];

      labels.forEach(function (labelName, index) {
        if (!values[index]) return;
        selections[normalizeOptionKey(labelName)] = String(values[index]).trim();
      });

      return Object.keys(selections).length ? selections : null;
    };

    const variantMatchesSelections = function (variant, selections, requireAvailable) {
      if (!variant || !variant.options || !optionNames.length) return false;
      if (requireAvailable && !variant.available) return false;

      return optionNames.every(function (name, index) {
        const key = normalizeOptionKey(name);
        const selected = selections[key];
        if (!selected) return true;
        const variantValue = variant.options[index];
        if (!variantValue) return false;
        return String(variantValue).toLowerCase() === selected.toLowerCase();
      });
    };

    const pickVariantFromSelections = function (selections, requireAvailable) {
      if (!selections || !variants.length) return null;

      const availableMatch = variants.find(function (variant) {
        return variantMatchesSelections(variant, selections, true);
      });
      if (availableMatch) return availableMatch.id;

      if (requireAvailable) return null;

      const anyMatch = variants.find(function (variant) {
        return variantMatchesSelections(variant, selections, false);
      });
      return anyMatch ? anyMatch.id : null;
    };

    const pickVariantSync = function () {
      const pdpSelections = getPdpSelections();
      if (!Object.keys(pdpSelections).length) return null;
      return pickVariantFromSelections(pdpSelections, true);
    };

    const pickVariantFallback = function () {
      const defaultId = Number(addBtn.getAttribute('data-default-variant')) || null;
      if (defaultId) {
        const defaultVariant = variants.find(function (variant) {
          return Number(variant.id) === defaultId;
        });
        if (defaultVariant && defaultVariant.available) return defaultId;
      }

      const firstAvailable = variants.find(function (variant) {
        return variant.available;
      });
      return firstAvailable ? firstAvailable.id : null;
    };

    const resolveVariantId = function (cart) {
      const pdpSelections = getPdpSelections();
      const hasPdpSelections = Object.keys(pdpSelections).length > 0;
      const syncId = pickVariantSync();

      if (syncId) return Promise.resolve(syncId);
      if (hasPdpSelections) return Promise.resolve(null);

      if (cart) {
        const cartSelections = getCartSelections(cart);
        if (cartSelections) {
          const cartMatch = pickVariantFromSelections(cartSelections, true);
          if (cartMatch) return Promise.resolve(cartMatch);
        }
      }
      return Promise.resolve(pickVariantFallback());
    };

    const resolveMainVariantId = function () {
      const select = document.querySelector('[data-pdp-select]');
      if (select && select.value) return Number(select.value);

      const pdpSelections = getPdpSelections();
      if (Object.keys(pdpSelections).length && mainVariants.length && optionNames.length) {
        const match = mainVariants.find(function (variant) {
          if (!variant.available || !variant.options) return false;
          return optionNames.every(function (name, index) {
            const key = normalizeOptionKey(name);
            const selected = pdpSelections[key];
            if (!selected) return true;
            return (
              String(variant.options[index] || '').toLowerCase() ===
              String(selected).toLowerCase()
            );
          });
        });
        if (match) return Number(match.id);
      }

      return Number(addBtn.getAttribute('data-main-default-variant')) || null;
    };

    const cartHasMainProduct = function (cart) {
      const productId = Number(addBtn.getAttribute('data-main-product-id')) || null;
      if (!productId || !cart || !cart.items) return false;
      return cart.items.some(function (item) {
        return Number(item.product_id) === productId;
      });
    };

    let pending = false;
    const reset = function () {
      addBtn.classList.remove('is-loading', 'is-added');
      if (label) label.textContent = text('add', 'Add matching piece');
      pending = false;
    };

    addBtn.addEventListener('click', function () {
      if (pending || addBtn.disabled) return;

      pending = true;
      addBtn.classList.add('is-loading');
      if (label) label.textContent = text('loading', 'Adding…');

      const api = window.oneraCartCore;
      if (!api) {
        reset();
        return;
      }

      api.clearError(root);

      api
        .fetchCart()
        .catch(function () {
          return { items: [] };
        })
        .then(function (cart) {
          return resolveVariantId(cart).then(function (matchId) {
            return { cart: cart, matchId: matchId };
          });
        })
        .then(function (resolved) {
          if (!resolved || !resolved.matchId) {
            const message = text(
              'unavailable',
              'This size is unavailable in the matching piece.'
            );
            api.showError(root, message);
            if (status) status.textContent = message;
            reset();
            return null;
          }

          const mainProductId = Number(addBtn.getAttribute('data-main-product-id')) || null;
          const matchProductId = Number(addBtn.getAttribute('data-match-product-id')) || null;
          const setRole = addBtn.getAttribute('data-set-role') || null;
          const meta =
            setRole || (mainProductId && matchProductId)
              ? {
                  setRole: setRole,
                  productId: matchProductId,
                  matchProductId: mainProductId,
                  matchHandle: addBtn.getAttribute('data-main-handle') || null,
                  productHandle: addBtn.getAttribute('data-match-handle') || null
                }
              : null;

          const items = [{ id: Number(resolved.matchId), quantity: 1 }];
          if (!cartHasMainProduct(resolved.cart)) {
            const mainId = resolveMainVariantId();
            if (mainId) {
              items.unshift({ id: Number(mainId), quantity: 1 });
            }
          }

          return api.addItems(items, 'complete-look', meta);
        })
        .then(function (result) {
          if (!result) return;
          addBtn.classList.remove('is-loading');
          addBtn.classList.add('is-added');
          if (label) label.textContent = text('added', 'Added');
          if (status) status.textContent = text('added', 'Added');
          window.setTimeout(reset, 1800);
        })
        .catch(function (error) {
          var message = api.getErrorMessage(error, 'add');
          api.showError(root, message);
          if (status) status.textContent = message;
          reset();
        });
    });
  };

  /* Re-bind scroll reveal and section interactions after theme editor AJAX
     section swaps (shopify:section:load). Without this, .onera-reveal stays
     at opacity: 0 and the preview looks blank after any setting change. */
  const initThemeEditorSection = function (sectionEl) {
    if (!sectionEl) return;
    initImageSkeletons(sectionEl);
    initReveal(sectionEl);
    initGetTheLook(sectionEl);
    initGetTheLookSwitcher(sectionEl);
    initCompleteLook();
  };

  const init = function () {
    initImageSkeletons();
    initReveal();
    initHeroSlideshow();
    initParallax();
    initGetTheLook();
    initGetTheLookSwitcher();
    initProductForm();
    initProductGallery();
    initAccordion();
    initSizeGuide();
    initCompleteLook();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initThemeEditorSection(event.target);
  });
})();
